/**
 * Force Change Password Page - HYB MASTER SHOP
 * For admins who must change password on first login (recovery admin)
 */

import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Shield } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { forceChangeFirstPassword } from '../api/auth';
import logoUrl from '../assets/logo.svg';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';
import { cn } from '../utils/responsive';
import { authLogger } from '../utils/logger';

interface ForceChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ForceChangePasswordPage() {
  const isMobileOrTablet = useIsMobileOrTablet();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const username = location.state?.username;

  const { register, handleSubmit, watch, formState: { errors } } = useForm<ForceChangePasswordForm>();

  // Redirect if no username provided
  if (!username) {
    return <Navigate to="/login" replace />;
  }

  const newPassword = watch('newPassword');

  const onSubmit = async (data: ForceChangePasswordForm) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('รหัสผ่านใหม่และยืนยันรหัสผ่านไม่ตรงกัน');
      return;
    }

    setIsChanging(true);

    try {
      await forceChangeFirstPassword({
        username,
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      toast.success('เปลี่ยนรหัสผ่านสำเร็จ! กรุณาเข้าสู่ระบบอีกครั้ง');

      // Redirect to login page
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    } catch (error: any) {
      authLogger.error('Force change password error:', error);
      // Handle error response properly - backend sends { error: { code, message } }
      const errorData = error?.response?.data?.error;
      const message = typeof errorData === 'string'
        ? errorData
        : errorData?.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน';
      toast.error(message);
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-orange-400 to-orange-500 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4 lg:py-12 lg:px-8">
      <div className={cn(
        "w-full",
        isMobileOrTablet ? "max-w-sm" : "max-w-md"
      )}>
        {/* Force Change Password Card */}
        <div className={cn(
          "bg-white dark:bg-gray-800 rounded-2xl shadow-2xl",
          isMobileOrTablet ? "p-6" : "p-8"
        )}>
          {/* Header */}
          <div className={cn(
            "text-center",
            isMobileOrTablet ? "mb-6" : "mb-8"
          )}>
            <img
              src={logoUrl}
              alt="HYB Master Shop"
              className={cn(
                "mx-auto mb-4 w-auto",
                isMobileOrTablet ? "h-12" : "h-16"
              )}
            />
            <div className={cn(
              "mb-4",
              isMobileOrTablet && "mb-3"
            )}>
              <Shield className={cn(
                "mx-auto text-orange-500",
                isMobileOrTablet ? "h-10 w-10" : "h-12 w-12"
              )} />
            </div>
            <h2 className={cn(
              "font-bold text-stone-900 dark:text-gray-100 mb-2",
              isMobileOrTablet ? "text-xl" : "text-3xl"
            )}>
              เปลี่ยนรหัสผ่านบังคับ
            </h2>
            <p className={cn(
              "text-stone-600 dark:text-gray-400",
              isMobileOrTablet ? "text-sm" : "text-base"
            )}>
              เพื่อความปลอดภัย กรุณาเปลี่ยนรหัสผ่านเริ่มต้น
            </p>
            <p className={cn(
              "text-stone-500 dark:text-gray-500 mt-2",
              isMobileOrTablet ? "text-xs" : "text-sm"
            )}>
              ผู้ใช้: <span className="font-semibold">{username}</span>
            </p>
          </div>

          {/* Force Change Password Form */}
          <form onSubmit={handleSubmit(onSubmit)} className={cn(
            "space-y-5",
            !isMobileOrTablet && "space-y-6"
          )}>
            {/* Current Password Field */}
            <div>
              <label htmlFor="currentPassword" className={cn(
                "block font-medium text-stone-700 dark:text-gray-300 mb-2",
                isMobileOrTablet ? "text-xs" : "text-sm"
              )}>
                รหัสผ่านปัจจุบัน
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-stone-400 dark:text-gray-500" />
                </div>
                <input
                  {...register('currentPassword', {
                    required: 'กรุณาใส่รหัสผ่านปัจจุบัน',
                  })}
                  type={showCurrentPassword ? 'text' : 'password'}
                  className={cn(
                    "block w-full pl-10 pr-10 border border-stone-300 dark:border-gray-600 rounded-lg",
                    "text-stone-900 dark:text-gray-100 placeholder:text-stone-400 dark:placeholder:text-gray-500",
                    "bg-white dark:bg-gray-900",
                    "focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 focus:border-yellow-500 dark:focus:border-orange-500",
                    "transition-all duration-200",
                    isMobileOrTablet ? "py-2.5 text-sm" : "py-3 text-base"
                  )}
                  placeholder="ใส่รหัสผ่านเริ่มต้น"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center touch-target"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-5 w-5 text-stone-400 dark:text-gray-500 hover:text-stone-600 dark:hover:text-gray-400 transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-stone-400 dark:text-gray-500 hover:text-stone-600 dark:hover:text-gray-400 transition-colors" />
                  )}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{String(errors.currentPassword?.message || '')}</p>
              )}
            </div>

            {/* New Password Field */}
            <div>
              <label htmlFor="newPassword" className={cn(
                "block font-medium text-stone-700 dark:text-gray-300 mb-2",
                isMobileOrTablet ? "text-xs" : "text-sm"
              )}>
                รหัสผ่านใหม่
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-stone-400 dark:text-gray-500" />
                </div>
                <input
                  {...register('newPassword', {
                    required: 'กรุณาใส่รหัสผ่านใหม่',
                    minLength: { value: 8, message: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร' },
                    pattern: {
                      value: /^(?=.*[A-Za-z])(?=.*\d).{8,}$/,
                      message: 'รหัสผ่านต้องมีทั้งตัวอักษรและตัวเลข'
                    }
                  })}
                  type={showNewPassword ? 'text' : 'password'}
                  className={cn(
                    "block w-full pl-10 pr-10 border border-stone-300 dark:border-gray-600 rounded-lg",
                    "text-stone-900 dark:text-gray-100 placeholder:text-stone-400 dark:placeholder:text-gray-500",
                    "bg-white dark:bg-gray-900",
                    "focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 focus:border-yellow-500 dark:focus:border-orange-500",
                    "transition-all duration-200",
                    isMobileOrTablet ? "py-2.5 text-sm" : "py-3 text-base"
                  )}
                  placeholder="ใส่รหัสผ่านใหม่"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center touch-target"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5 text-stone-400 dark:text-gray-500 hover:text-stone-600 dark:hover:text-gray-400 transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-stone-400 dark:text-gray-500 hover:text-stone-600 dark:hover:text-gray-400 transition-colors" />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{String(errors.newPassword?.message || '')}</p>
              )}
              <p className="mt-1 text-xs text-stone-500 dark:text-gray-400">
                รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร และมีทั้งตัวอักษรและตัวเลข
              </p>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className={cn(
                "block font-medium text-stone-700 dark:text-gray-300 mb-2",
                isMobileOrTablet ? "text-xs" : "text-sm"
              )}>
                ยืนยันรหัสผ่านใหม่
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-stone-400 dark:text-gray-500" />
                </div>
                <input
                  {...register('confirmPassword', {
                    required: 'กรุณายืนยันรหัสผ่านใหม่',
                    validate: value => value === newPassword || 'รหัสผ่านไม่ตรงกัน'
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={cn(
                    "block w-full pl-10 pr-10 border border-stone-300 dark:border-gray-600 rounded-lg",
                    "text-stone-900 dark:text-gray-100 placeholder:text-stone-400 dark:placeholder:text-gray-500",
                    "bg-white dark:bg-gray-900",
                    "focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 focus:border-yellow-500 dark:focus:border-orange-500",
                    "transition-all duration-200",
                    isMobileOrTablet ? "py-2.5 text-sm" : "py-3 text-base"
                  )}
                  placeholder="ยืนยันรหัสผ่านใหม่"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center touch-target"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-stone-400 dark:text-gray-500 hover:text-stone-600 dark:hover:text-gray-400 transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-stone-400 dark:text-gray-500 hover:text-stone-600 dark:hover:text-gray-400 transition-colors" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{String(errors.confirmPassword?.message || '')}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isChanging}
              className={cn(
                "w-full flex justify-center items-center",
                "px-4 border border-transparent rounded-lg shadow-sm",
                "text-white font-semibold",
                "bg-gradient-to-r from-yellow-400 to-orange-500",
                "hover:from-orange-500 hover:to-yellow-400 hover:shadow-accent",
                "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500",
                "transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm",
                "touch-target",
                isMobileOrTablet ? "py-2.5 text-sm" : "py-3 text-base"
              )}
            >
              {isChanging ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  กำลังเปลี่ยนรหัสผ่าน...
                </>
              ) : (
                'เปลี่ยนรหัสผ่าน'
              )}
            </button>
          </form>

          {/* Warning */}
          <div className={cn(
            "p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg",
            isMobileOrTablet ? "mt-5" : "mt-6"
          )}>
            <p className={cn(
              "text-amber-800 dark:text-amber-200",
              isMobileOrTablet ? "text-xs" : "text-sm"
            )}>
              <strong>คำเตือน:</strong> หลังจากเปลี่ยนรหัสผ่านสำเร็จ คุณจะต้องเข้าสู่ระบบใหม่ด้วยรหัสผ่านใหม่
            </p>
          </div>

          {/* Footer */}
          <div className={cn(
            "text-center text-stone-500 dark:text-gray-500",
            isMobileOrTablet ? "mt-6 text-xs" : "mt-8 text-sm"
          )}>
            <p>
              © 2024 HYB MASTER SHOP - Web Management Dashboard
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
